import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as fabric from 'fabric';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ChevronLeft, ChevronRight, X, Upload, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { RibbonMenu } from './RibbonMenu';
import { SlideThumbnails } from './SlideThumbnails';
import { PropertiesPanel } from './PropertiesPanel';
import { TRANSITION_CSS, runObjectAnimations, getTransitionAnimation } from './slideAnimations';

const CW = 960;
const CH = 540;
const LS_KEY = 'slide-designer-draft';
const EXTRA_PROPS = [
  'isMediaEmbed', 'mediaType', 'mediaUrl', 'mediaAutoplay', 'mediaHidden',
  'animationType', 'animationDuration', 'animationDelay',
  'isLabeledShape',
  'isTable', 'tableRows', 'tableCols', 'isTableCell', 'isTableText', 'cellRow', 'cellCol',
];
const API = process.env.REACT_APP_BACKEND_URL;
const uid = () => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
const makeSlide = () => ({ id: uid(), canvas: null, transition: { type: 'none', duration: 500 } });

/* ── URL Detection Helpers ── */
const isYouTubeUrl = (url) => {
  if (!url) return false;
  return /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)/.test(url);
};

const isVimeoUrl = (url) => {
  if (!url) return false;
  return /(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)/.test(url);
};

const getYouTubeEmbedUrl = (url) => {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0&modestbranding=1` : null;
};

const getVimeoEmbedUrl = (url) => {
  const match = url.match(/(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/);
  return match ? `https://player.vimeo.com/video/${match[1]}?autoplay=1` : null;
};

const isEmbedUrl = (url) => isYouTubeUrl(url) || isVimeoUrl(url);

const getEmbedUrl = (url) => {
  if (isYouTubeUrl(url)) return getYouTubeEmbedUrl(url);
  if (isVimeoUrl(url)) return getVimeoEmbedUrl(url);
  return null;
};

/* ── Custom Video Player (Play/Pause only, no download) ── */
const MinimalVideoPlayer = ({ src, autoPlay, className, testId }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(autoPlay);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.play().catch(() => setPlaying(false));
      } else {
        videoRef.current.pause();
      }
    }
  }, [playing]);

  const togglePlay = () => setPlaying((p) => !p);

  return (
    <div 
      className={`relative ${className || ''}`} 
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
      onClick={togglePlay}
      style={{ cursor: 'pointer' }}
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay={autoPlay}
        playsInline
        muted={autoPlay}
        className="w-full h-full object-contain bg-black rounded"
        data-testid={testId}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <div 
        className={`absolute inset-0 flex items-center justify-center transition-opacity ${showControls || !playing ? 'opacity-100' : 'opacity-0'}`}
        style={{ pointerEvents: 'none' }}
      >
        <div className="bg-black/50 rounded-full p-3">
          {playing ? (
            <Pause className="w-8 h-8 text-white" />
          ) : (
            <Play className="w-8 h-8 text-white" />
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Embed Player (YouTube/Vimeo) ── */
const EmbedPlayer = ({ url, className, testId }) => {
  const embedUrl = getEmbedUrl(url);
  if (!embedUrl) return null;
  
  return (
    <iframe
      src={embedUrl}
      className={`w-full h-full rounded ${className || ''}`}
      data-testid={testId}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      title="Embedded video"
    />
  );
};

// Suppress Fabric.js image loading errors (CORS issues with cross-origin images)
if (typeof window !== 'undefined' && !window.__fabricErrorHandlerSet) {
  window.__fabricErrorHandlerSet = true;
  const suppressFabricError = (e) => {
    const msg = e.message || e.reason?.message || '';
    if (msg.includes('fabric: Error loading') || msg.includes('fabric:')) {
      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
      console.warn('Fabric image loading error (likely CORS):', msg);
      return true;
    }
  };
  window.addEventListener('error', suppressFabricError);
  window.addEventListener('unhandledrejection', (e) => {
    const msg = e.reason?.message || String(e.reason) || '';
    if (msg.includes('fabric') || msg.includes('Error loading')) {
      e.preventDefault();
      console.warn('Fabric promise rejection (likely CORS):', msg);
    }
  });
}

/* Ensure interactive groups restore correctly after JSON load */
const fixInteractiveGroups = (canvas) => {
  canvas.getObjects().forEach((obj) => {
    if (obj.type === 'group' && (obj.isLabeledShape || obj.isTable)) {
      obj.set({ interactive: true, subTargetCheck: true });
    }
  });
};

/**
 * Fabric.js v7 loadFromJSON drops custom properties not in its schema.
 * This restores them from the raw JSON data by matching objects by index.
 */
const restoreCustomProps = (canvas, rawData) => {
  const objs = canvas.getObjects();
  const rawObjs = rawData?.objects;
  if (!rawObjs) return;
  rawObjs.forEach((raw, i) => {
    if (i >= objs.length) return;
    EXTRA_PROPS.forEach((prop) => {
      if (raw[prop] !== undefined) objs[i][prop] = raw[prop];
    });
  });
};

export const SlideEditor = ({ initialData, onSave, onClose }) => {
  const canvasMountRef = useRef(null);
  const fc = useRef(null);
  const wrapRef = useRef(null);
  const imgInput = useRef(null);
  const bgImgInput = useRef(null);
  const mediaFileInput = useRef(null);
  const previewMountRef = useRef(null);
  const previewFc = useRef(null);
  const autoSaveTimer = useRef(null);
  const historyStack = useRef([]);
  const historyIdx = useRef(-1);
  const skipHistory = useRef(false);

  /* ── Slide state ── */
  const [slides, _setSlides] = useState(() => {
    const parse = (raw) => {
      try {
        const d = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (d?.slides?.length) return d.slides.map((s) => ({ ...s, transition: s.transition || { type: 'none', duration: 500 } }));
      } catch { /* ignore */ }
      return null;
    };
    if (initialData) { const p = parse(initialData); if (p) return p; }
    const draft = parse(localStorage.getItem(LS_KEY));
    if (draft) { setTimeout(() => toast.info('Restored auto-saved draft'), 500); return draft; }
    return [makeSlide()];
  });
  const slidesRef = useRef(slides);
  const setSlides = useCallback((val) => {
    const next = typeof val === 'function' ? val(slidesRef.current) : val;
    slidesRef.current = next;
    _setSlides(next);
  }, []);

  const [activeIdx, _setActiveIdx] = useState(0);
  const activeRef = useRef(0);
  const setActiveIdx = useCallback((v) => { activeRef.current = v; _setActiveIdx(v); }, []);

  const [, forceRender] = useState(0);
  const rerender = useCallback(() => forceRender((v) => v + 1), []);

  const [scale, setScale] = useState(0.7);
  const [thumbs, setThumbs] = useState({});
  const [bgColor, setBgColor] = useState('#ffffff');
  const [preview, setPreview] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [showImgPanel, setShowImgPanel] = useState(false);
  const [showMediaPanel, setShowMediaPanel] = useState(null);
  const [mediaUrlInput, setMediaUrlInput] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [autoSaved, setAutoSaved] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [editorMedia, setEditorMedia] = useState(null);
  const [previewMediaItems, setPreviewMediaItems] = useState([]);
  const [previewTransAnim, setPreviewTransAnim] = useState('');
  
  /* ── Inline cell editor state ── */
  const [cellEditor, setCellEditor] = useState(null); // { table, row, col, text, rect }
  const cellEditorRef = useRef(null);

  /* ── Lock scroll ── */
  useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }, []);

  /* ── History ── */
  const pushHistory = useCallback(() => {
    if (!fc.current || skipHistory.current) return;
    const json = JSON.stringify(fc.current.toObject(EXTRA_PROPS));
    historyStack.current = historyStack.current.slice(0, historyIdx.current + 1);
    historyStack.current.push(json);
    if (historyStack.current.length > 50) historyStack.current.shift();
    historyIdx.current = historyStack.current.length - 1;
  }, []);

  const undo = useCallback(() => {
    if (!fc.current || historyIdx.current <= 0) return;
    historyIdx.current--;
    skipHistory.current = true;
    fc.current.loadFromJSON(JSON.parse(historyStack.current[historyIdx.current])).then(() => {
      restoreCustomProps(fc.current, JSON.parse(historyStack.current[historyIdx.current]));
      fixInteractiveGroups(fc.current);
      fc.current.renderAll();
      skipHistory.current = false;
      rerender();
    }).catch((e) => { console.warn('Undo load error:', e); skipHistory.current = false; });
  }, [rerender]);

  const redo = useCallback(() => {
    if (!fc.current || historyIdx.current >= historyStack.current.length - 1) return;
    historyIdx.current++;
    skipHistory.current = true;
    fc.current.loadFromJSON(JSON.parse(historyStack.current[historyIdx.current])).then(() => {
      restoreCustomProps(fc.current, JSON.parse(historyStack.current[historyIdx.current]));
      fixInteractiveGroups(fc.current);
      fc.current.renderAll();
      skipHistory.current = false;
      rerender();
    }).catch((e) => { console.warn('Redo load error:', e); skipHistory.current = false; });
  }, [rerender]);

  /* ── Canvas Init ── */
  const mountedRef = useRef(false);
  
  useEffect(() => {
    const mount = canvasMountRef.current;
    if (!mount) return;
    
    /* Track if this effect instance is still active (for StrictMode) */
    let isActive = true;
    mountedRef.current = true;
    
    /* Create a fresh canvas element inside the mount div each time.
       This prevents Fabric.dispose() from invalidating a shared ref. */
    mount.innerHTML = '';
    const el = document.createElement('canvas');
    el.setAttribute('data-testid', 'slide-canvas');
    mount.appendChild(el);
    const c = new fabric.Canvas(el, {
      width: CW, height: CH, backgroundColor: '#ffffff', preserveObjectStacking: true,
    });
    fc.current = c;
    c.on('selection:created', rerender);
    c.on('selection:updated', rerender);
    c.on('selection:cleared', rerender);
    c.on('object:modified', () => { if (isActive) { pushHistory(); rerender(); scheduleAutoSave(); } });
    c.on('object:added', () => { if (isActive && !skipHistory.current) { pushHistory(); scheduleAutoSave(); } });
    c.on('object:removed', () => { if (isActive && !skipHistory.current) { pushHistory(); scheduleAutoSave(); } });

    /* Double-click: text-in-shapes, media preview, or table cell editing */
    c.on('mouse:dblclick', (opt) => {
      if (!isActive) return;
      const target = opt.target;
      if (!target) return;
      
      // Media embed preview
      if (target.isMediaEmbed) {
        setEditorMedia({ type: target.mediaType, url: target.mediaUrl, autoplay: true });
        return;
      }
      
      // Table cell editing - show inline editor
      if (target.isTable) {
        const e = opt.e;
        const pointer = { x: e.offsetX, y: e.offsetY };
        const objects = target.getObjects();
        
        // Transform pointer to group coordinates
        const groupMatrix = target.calcTransformMatrix();
        const invertedMatrix = fabric.util.invertTransform(groupMatrix);
        const localPoint = fabric.util.transformPoint(
          new fabric.Point(pointer.x, pointer.y),
          invertedMatrix
        );
        
        // Find the cell rect at clicked position
        let clickedCell = null;
        for (const obj of objects) {
          if (obj.isTableCell) {
            if (localPoint.x >= obj.left && localPoint.x <= obj.left + obj.width &&
                localPoint.y >= obj.top && localPoint.y <= obj.top + obj.height) {
              clickedCell = obj;
              break;
            }
          }
        }
        
        if (clickedCell) {
          const textObj = objects.find(o => 
            o.isTableText && o.cellRow === clickedCell.cellRow && o.cellCol === clickedCell.cellCol
          );
          
          if (textObj) {
            // Get the canvas element and its bounding rect
            const canvasEl = fc.current?.lowerCanvasEl;
            if (!canvasEl) return;
            const canvasRect = canvasEl.getBoundingClientRect();
            
            // Calculate cell position in canvas coordinates
            const cellMatrix = target.calcTransformMatrix();
            const cellLeft = clickedCell.left;
            const cellTop = clickedCell.top;
            
            // Transform to screen coordinates
            const topLeft = fabric.util.transformPoint(
              new fabric.Point(cellLeft, cellTop),
              cellMatrix
            );
            
            // Calculate actual screen position
            const scaleX = canvasRect.width / CW;
            const scaleY = canvasRect.height / CH;
            
            // Show inline editor
            setCellEditor({
              table: target,
              row: clickedCell.cellRow,
              col: clickedCell.cellCol,
              text: textObj.text || '',
              textObj: textObj,
              rect: {
                left: canvasRect.left + topLeft.x * scaleX,
                top: canvasRect.top + topLeft.y * scaleY,
                width: clickedCell.width * target.scaleX * scaleX,
                height: clickedCell.height * target.scaleY * scaleY,
              },
              isHeader: clickedCell.cellRow === 0,
            });
          }
        }
        return;
      }
      
      // Shape to labeled shape conversion
      if (['rect', 'circle', 'triangle'].includes(target.type)) {
        convertToLabeledShape(target);
      }
    });

    /* Draw play icon overlay on media embeds */
    c.on('after:render', () => {
      if (!isActive || !c.lowerCanvasEl) return;
      const ctx = c.contextContainer;
      if (!ctx) return;
      c.getObjects().forEach(obj => {
        if (!obj.isMediaEmbed) return;
        const bound = obj.getBoundingRect();
        const cx = bound.left + bound.width / 2;
        const cy = bound.top + bound.height / 2;
        ctx.save();
        if (obj.mediaHidden) {
          /* Hidden media: dashed border + label */
          ctx.setLineDash([6, 4]);
          ctx.strokeStyle = 'rgba(255,255,255,0.6)';
          ctx.lineWidth = 2;
          ctx.strokeRect(bound.left, bound.top, bound.width, bound.height);
          ctx.setLineDash([]);
          ctx.fillStyle = 'rgba(255,255,255,0.8)';
          ctx.font = 'bold 11px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('Hidden on Slide', cx, cy - 4);
          ctx.font = '10px Arial';
          ctx.fillStyle = 'rgba(255,255,255,0.55)';
          ctx.fillText(obj.mediaType === 'audio' ? 'Audio will autoplay' : 'Video will autoplay', cx, cy + 12);
        } else if (obj.mediaType === 'audio') {
          /* Compact audio player bar */
          const bx = bound.left + 12;
          const by = cy;
          /* Play circle */
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          ctx.beginPath();
          ctx.arc(bx + 6, by, 10, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#312e81';
          ctx.beginPath();
          ctx.moveTo(bx + 3, by - 5);
          ctx.lineTo(bx + 11, by);
          ctx.lineTo(bx + 3, by + 5);
          ctx.closePath();
          ctx.fill();
          /* Progress track */
          const trackX = bx + 24;
          const trackW = bound.width - 52;
          ctx.fillStyle = 'rgba(255,255,255,0.25)';
          ctx.beginPath();
          ctx.roundRect(trackX, by - 2.5, trackW, 5, 2.5);
          ctx.fill();
          ctx.fillStyle = 'rgba(255,255,255,0.7)';
          ctx.beginPath();
          ctx.roundRect(trackX, by - 2.5, trackW * 0.35, 5, 2.5);
          ctx.fill();
          /* Scrubber dot */
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(trackX + trackW * 0.35, by, 5, 0, Math.PI * 2);
          ctx.fill();
        } else {
          /* Video: play icon + label */
          ctx.fillStyle = 'rgba(0,0,0,0.55)';
          ctx.beginPath();
          ctx.arc(cx, cy, 26, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.moveTo(cx - 9, cy - 14);
          ctx.lineTo(cx + 15, cy);
          ctx.lineTo(cx - 9, cy + 14);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = 'rgba(255,255,255,0.85)';
          ctx.font = '11px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('Video - Double-click to play', cx, cy + 44);
        }
        ctx.restore();
      });
    });

    const first = slidesRef.current[0];
    if (first?.canvas) {
      /* Check if canvas is still valid before loading */
      if (!c.lowerCanvasEl || !isActive) return;
      c.loadFromJSON(first.canvas).then(() => {
        if (!isActive || !c.lowerCanvasEl) return;
        restoreCustomProps(c, first.canvas);
        fixInteractiveGroups(c);
        c.renderAll();
        setBgColor(c.backgroundColor || '#ffffff');
        pushHistory();
      }).catch(() => { /* ignore load errors during unmount */ });
    } else {
      pushHistory();
    }
    return () => {
      /* Mark this effect instance as inactive */
      isActive = false;
      mountedRef.current = false;
      
      /* Cleanup: nullify ref first to prevent any further operations */
      const canvas = fc.current;
      fc.current = null;
      
      /* Clear the mount div immediately to remove the canvas element */
      if (mount) mount.innerHTML = '';
      
      /* Dispose the canvas asynchronously after the DOM is cleared */
      if (canvas) {
        setTimeout(() => {
          try { canvas.dispose(); } catch { /* ignore dispose errors */ }
        }, 0);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Scale ── */
  useEffect(() => {
    if (!wrapRef.current) return;
    const calc = () => {
      const el = wrapRef.current;
      if (!el) return;
      const s = Math.min((el.clientWidth - 32) / CW, (el.clientHeight - 32) / CH, 1);
      setScale(s);
      setZoomLevel(Math.round(s * 100));
    };
    calc();
    const obs = new ResizeObserver(calc);
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  /* ── Auto-save ── */
  const scheduleAutoSave = useCallback(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      if (!fc.current) return;
      const s = [...slidesRef.current];
      s[activeRef.current] = { ...s[activeRef.current], canvas: fc.current.toObject(EXTRA_PROPS) };
      try {
        localStorage.setItem(LS_KEY, JSON.stringify({ slides: s }));
        setAutoSaved(true);
        setTimeout(() => setAutoSaved(false), 2000);
      } catch { /* storage full */ }
    }, 2000);
  }, []);

  /* ── Thumbnails ── */
  const captureThumbnail = useCallback((idx) => {
    if (!fc.current) return;
    try {
      const url = fc.current.toDataURL({ format: 'jpeg', quality: 0.3, multiplier: 0.15 });
      setThumbs((p) => ({ ...p, [idx]: url }));
    } catch { /* ignore */ }
  }, []);

  /* ── Slide management ── */
  const switchSlide = useCallback(async (newIdx) => {
    if (newIdx === activeRef.current || !fc.current || newIdx < 0 || newIdx >= slidesRef.current.length) return;
    const c = fc.current;
    if (!c.lowerCanvasEl) return; // Canvas disposed
    captureThumbnail(activeRef.current);
    const updated = [...slidesRef.current];
    updated[activeRef.current] = { ...updated[activeRef.current], canvas: c.toObject(EXTRA_PROPS) };
    if (!c.lowerCanvasEl) return; // Check again before clear
    c.clear();
    c.backgroundColor = '#ffffff';
    if (updated[newIdx]?.canvas) {
      try {
        await c.loadFromJSON(updated[newIdx].canvas);
        restoreCustomProps(c, updated[newIdx].canvas);
        fixInteractiveGroups(c);
      } catch (e) {
        console.warn('goToSlide load error:', e);
      }
    }
    c.discardActiveObject();
    c.renderAll();
    setBgColor(c.backgroundColor || '#ffffff');
    setSlides(updated);
    setActiveIdx(newIdx);
    historyStack.current = [JSON.stringify(c.toObject(EXTRA_PROPS))];
    historyIdx.current = 0;
    rerender();
  }, [captureThumbnail, setSlides, setActiveIdx, rerender]);

  const addSlide = useCallback(() => {
    if (!fc.current || !fc.current.lowerCanvasEl) return;
    captureThumbnail(activeRef.current);
    const updated = [...slidesRef.current];
    updated[activeRef.current] = { ...updated[activeRef.current], canvas: fc.current.toObject(EXTRA_PROPS) };
    updated.push(makeSlide());
    if (!fc.current.lowerCanvasEl) return; // Check again before clear
    fc.current.clear();
    fc.current.backgroundColor = '#ffffff';
    fc.current.renderAll();
    setBgColor('#ffffff');
    setSlides(updated);
    setActiveIdx(updated.length - 1);
    historyStack.current = [JSON.stringify(fc.current.toObject(EXTRA_PROPS))];
    historyIdx.current = 0;
    rerender();
  }, [captureThumbnail, setSlides, setActiveIdx, rerender]);

  const duplicateSlide = useCallback(() => {
    if (!fc.current) return;
    const curData = fc.current.toObject(EXTRA_PROPS);
    captureThumbnail(activeRef.current);
    const updated = [...slidesRef.current];
    const curSlide = updated[activeRef.current];
    updated[activeRef.current] = { ...curSlide, canvas: curData };
    const newSlide = { id: uid(), canvas: JSON.parse(JSON.stringify(curData)), transition: { ...(curSlide.transition || { type: 'none', duration: 500 }) } };
    updated.splice(activeRef.current + 1, 0, newSlide);
    setSlides(updated);
    switchSlide(activeRef.current + 1);
  }, [captureThumbnail, setSlides, switchSlide]);

  const moveSlide = useCallback((dir) => {
    const idx = activeRef.current;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= slidesRef.current.length) return;
    const updated = [...slidesRef.current];
    updated[idx] = { ...updated[idx], canvas: fc.current?.toObject(EXTRA_PROPS) };
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setThumbs((p) => { const n = { ...p }; [n[idx], n[newIdx]] = [n[newIdx], n[idx]]; return n; });
    setSlides(updated);
    setActiveIdx(newIdx);
  }, [setSlides, setActiveIdx]);

  const deleteSlide = useCallback((idx) => {
    if (slidesRef.current.length <= 1) { toast.error('Cannot delete the only slide'); return; }
    const updated = [...slidesRef.current];
    if (idx !== activeRef.current) {
      updated[activeRef.current] = { ...updated[activeRef.current], canvas: fc.current?.toObject(EXTRA_PROPS) };
    }
    updated.splice(idx, 1);
    const newIdx = Math.min(idx <= activeRef.current ? Math.max(0, activeRef.current - 1) : activeRef.current, updated.length - 1);
    if (fc.current && fc.current.lowerCanvasEl) {
      if (updated[newIdx]?.canvas) {
        fc.current.loadFromJSON(updated[newIdx].canvas).then(() => { 
          if (!fc.current || !fc.current.lowerCanvasEl) return;
          restoreCustomProps(fc.current, updated[newIdx].canvas); 
          fixInteractiveGroups(fc.current); 
          fc.current.renderAll(); 
          setBgColor(fc.current.backgroundColor || '#ffffff'); 
        }).catch((e) => { console.warn('deleteSlide load error:', e); });
      } else {
        if (fc.current.lowerCanvasEl) {
          fc.current.clear(); fc.current.backgroundColor = '#ffffff'; fc.current.renderAll(); setBgColor('#ffffff');
        }
      }
    }
    setSlides(updated);
    setActiveIdx(newIdx);
    rerender();
  }, [setSlides, setActiveIdx, rerender]);

  /* ── Object tools ── */
  const addText = useCallback(() => {
    if (!fc.current) return;
    const t = new fabric.Textbox('Double-click to edit', {
      left: CW / 2 - 120, top: CH / 2 - 20, width: 240,
      fontSize: 28, fill: '#1a1a2e', fontFamily: 'Arial', textAlign: 'left',
    });
    fc.current.add(t);
    fc.current.setActiveObject(t);
    fc.current.renderAll();
  }, []);

  const addShape = useCallback((shape) => {
    if (!fc.current) return;
    let obj;
    const cx = CW / 2, cy = CH / 2;
    if (shape === 'rect') obj = new fabric.Rect({ left: cx - 75, top: cy - 50, width: 150, height: 100, fill: '#4f46e5', rx: 4, ry: 4 });
    else if (shape === 'circle') obj = new fabric.Circle({ left: cx - 40, top: cy - 40, radius: 40, fill: '#059669' });
    else if (shape === 'triangle') obj = new fabric.Triangle({ left: cx - 40, top: cy - 40, width: 80, height: 80, fill: '#dc2626' });
    else if (shape === 'line') obj = new fabric.Line([cx - 80, cy, cx + 80, cy], { stroke: '#333333', strokeWidth: 3 });
    if (obj) { fc.current.add(obj); fc.current.setActiveObject(obj); fc.current.renderAll(); }
  }, []);

  const addImageFromUrl = useCallback(async (url) => {
    if (!fc.current || !url) return;
    try {
      const img = await fabric.FabricImage.fromURL(url, { crossOrigin: 'anonymous' });
      const maxW = CW * 0.6, maxH = CH * 0.6;
      if (img.width > maxW || img.height > maxH) img.scale(Math.min(maxW / img.width, maxH / img.height));
      img.set({ left: CW / 2 - img.getScaledWidth() / 2, top: CH / 2 - img.getScaledHeight() / 2 });
      fc.current.add(img);
      fc.current.setActiveObject(img);
      fc.current.renderAll();
      setShowImgPanel(false);
      setImgUrl('');
    } catch { toast.error('Failed to load image. Check URL or CORS.'); }
  }, []);

  const handleImgUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const tid = toast.loading('Uploading image...');
    try {
      const form = new FormData(); form.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/upload/image`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.dismiss(tid);
      await addImageFromUrl(`${API}${res.data.url}`);
    } catch { toast.dismiss(tid); toast.error('Image upload failed'); }
  }, [addImageFromUrl]);

  const setImageAsBackground = useCallback(async () => {
    if (!fc.current) return;
    const obj = fc.current.getActiveObject();
    if (!obj || obj.type !== 'image') return;
    const src = obj.getSrc();
    fc.current.remove(obj);
    fc.current.discardActiveObject();
    try {
      const img = await fabric.FabricImage.fromURL(src, { crossOrigin: 'anonymous' });
      const s = Math.max(CW / img.width, CH / img.height);
      img.set({ scaleX: s, scaleY: s, originX: 'center', originY: 'center', left: CW / 2, top: CH / 2 });
      fc.current.backgroundImage = img;
      fc.current.renderAll();
      rerender();
      scheduleAutoSave();
      toast.success('Image set as background');
    } catch { toast.error('Failed to set background'); }
  }, [rerender, scheduleAutoSave]);

  const handleBgImgUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !fc.current) return;
    e.target.value = '';
    const tid = toast.loading('Uploading background image...');
    try {
      const form = new FormData(); form.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/upload/image`, form, { headers: { Authorization: `Bearer ${token}` } });
      const img = await fabric.FabricImage.fromURL(`${API}${res.data.url}`, { crossOrigin: 'anonymous' });
      const s = Math.max(CW / img.width, CH / img.height);
      img.set({ scaleX: s, scaleY: s, originX: 'center', originY: 'center', left: CW / 2, top: CH / 2 });
      fc.current.backgroundImage = img;
      fc.current.renderAll();
      scheduleAutoSave();
      toast.dismiss(tid);
      toast.success('Background image set');
    } catch { toast.dismiss(tid); toast.error('Background image upload failed'); }
  }, [scheduleAutoSave]);

  const addMediaEmbed = useCallback((type, url, autoplay = false) => {
    if (!fc.current || !url) return;
    /* Audio: light background in editor for visibility, transparent in preview/viewer. Video: dark rectangle. */
    const isAudio = type === 'audio';
    const w = isAudio ? 280 : 320;
    const h = isAudio ? 44 : 180;
    const rect = new fabric.Rect({
      left: CW / 2 - w / 2, top: CH / 2 - h / 2, width: w, height: h,
      fill: isAudio ? '#e0e7ff' : '#0f172a',  /* Light indigo bg for editor visibility, transparent in viewer */
      stroke: isAudio ? '#6366f1' : '#6366f1',
      strokeWidth: isAudio ? 2 : 2, rx: isAudio ? 22 : 8, ry: isAudio ? 22 : 8,
      isMediaEmbed: true, mediaType: type, mediaUrl: url, mediaAutoplay: autoplay,
    });
    fc.current.add(rect);
    fc.current.setActiveObject(rect);
    fc.current.renderAll();
    setShowMediaPanel(null);
    setMediaUrlInput('');
    toast.success(`${type === 'video' ? 'Video' : 'Audio'} embed added`);
  }, []);

  /* ── Table Insert ── */
  /* Tables are created as groups - double-click opens inline editor */
  const addTable = useCallback((rows, cols) => {
    if (!fc.current) return;
    const cellW = Math.min(100, (CW * 0.65) / cols);
    const cellH = 32;
    const tableW = cols * cellW;
    const tableH = rows * cellH;
    const ox = -tableW / 2;
    const oy = -tableH / 2;
    const children = [];
    
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const isHeader = r === 0;
        // Cell background rectangle
        children.push(new fabric.Rect({
          left: ox + c * cellW, 
          top: oy + r * cellH, 
          width: cellW, 
          height: cellH,
          fill: isHeader ? '#5b9bd5' : (r % 2 === 0 ? '#d6dce5' : '#ffffff'),
          stroke: '#9cb4ce', 
          strokeWidth: 1, 
          originX: 'left', 
          originY: 'top',
          isTableCell: true, 
          cellRow: r, 
          cellCol: c,
        }));
        // Text object (centered in cell)
        children.push(new fabric.Text('', {
          left: ox + c * cellW + cellW / 2, 
          top: oy + r * cellH + cellH / 2,
          fontSize: 11,
          fill: isHeader ? '#ffffff' : '#1e293b', 
          fontFamily: 'Arial',
          fontWeight: 'normal', 
          textAlign: 'center',
          originX: 'center',
          originY: 'center',
          isTableText: true, 
          cellRow: r, 
          cellCol: c,
        }));
      }
    }
    
    const group = new fabric.Group(children, {
      left: CW / 2, 
      top: CH / 2, 
      originX: 'center', 
      originY: 'center',
      isTable: true, 
      tableRows: rows, 
      tableCols: cols,
    });
    
    fc.current.add(group);
    fc.current.bringObjectToFront(group);
    fc.current.setActiveObject(group);
    fc.current.renderAll();
    toast.success(`${rows}×${cols} table inserted - Double-click cells to type`);
  }, []);

  const handleMediaFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !showMediaPanel) return;
    e.target.value = '';
    const tid = toast.loading(`Uploading ${showMediaPanel}...`);
    try {
      const form = new FormData(); form.append('file', file);
      const token = localStorage.getItem('token');
      const endpoint = showMediaPanel === 'video' ? '/api/upload/video' : '/api/upload/audio';
      const res = await axios.post(`${API}${endpoint}`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.dismiss(tid);
      addMediaEmbed(showMediaPanel, `${API}${res.data.url}`, false);
    } catch { toast.dismiss(tid); toast.error(`${showMediaPanel} upload failed`); }
  }, [showMediaPanel, addMediaEmbed]);

  const deleteSelected = useCallback(() => {
    if (!fc.current) return;
    const obj = fc.current.getActiveObject();
    if (!obj) return;
    if (obj.isEditing) obj.exitEditing();
    fc.current.remove(obj);
    fc.current.discardActiveObject();
    fc.current.renderAll();
    rerender();
  }, [rerender]);

  const duplicateSelected = useCallback(async () => {
    if (!fc.current) return;
    const obj = fc.current.getActiveObject();
    if (!obj) return;
    const clone = await obj.clone(EXTRA_PROPS);
    clone.set({ left: (obj.left || 0) + 20, top: (obj.top || 0) + 20 });
    if (clone.isLabeledShape && clone.type === 'group') {
      clone.set({ interactive: true, subTargetCheck: true });
    }
    fc.current.add(clone);
    fc.current.setActiveObject(clone);
    fc.current.renderAll();
  }, []);

  const bringForward = useCallback(() => {
    if (!fc.current) return;
    const obj = fc.current.getActiveObject();
    if (obj) { fc.current.bringObjectForward(obj); fc.current.renderAll(); }
  }, []);

  const sendBackward = useCallback(() => {
    if (!fc.current) return;
    const obj = fc.current.getActiveObject();
    if (obj) { fc.current.sendObjectBackwards(obj); fc.current.renderAll(); }
  }, []);

  const changeBgColor = useCallback((color) => {
    if (!fc.current) return;
    fc.current.backgroundColor = color;
    fc.current.renderAll();
    setBgColor(color);
    scheduleAutoSave();
  }, [scheduleAutoSave]);

  const clearBgImage = useCallback(() => {
    if (!fc.current) return;
    fc.current.backgroundImage = null;
    fc.current.renderAll();
    scheduleAutoSave();
  }, [scheduleAutoSave]);

  /* ── Save cell editor content ── */
  const saveCellEdit = useCallback((newText) => {
    if (!cellEditor || !fc.current) return;
    const { textObj } = cellEditor;
    if (textObj) {
      textObj.set('text', newText || '');
      fc.current.renderAll();
      pushHistory();
      scheduleAutoSave();
    }
    setCellEditor(null);
  }, [cellEditor, pushHistory, scheduleAutoSave]);

  /* ── Helper: Rebuild table from data ── */
  const rebuildTable = useCallback((tableData, position) => {
    if (!fc.current) return;
    
    const { rows, cols, cells, left, top, scaleX, scaleY } = tableData;
    const cellW = 100;
    const cellH = 32;
    const tableW = cols * cellW;
    const tableH = rows * cellH;
    const ox = -tableW / 2;
    const oy = -tableH / 2;
    const children = [];
    
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cellData = cells.find(cell => cell.row === r && cell.col === c);
        const isHeader = r === 0;
        
        children.push(new fabric.Rect({
          left: ox + c * cellW,
          top: oy + r * cellH,
          width: cellW,
          height: cellH,
          fill: cellData?.bgColor || (isHeader ? '#5b9bd5' : (r % 2 === 0 ? '#d6dce5' : '#ffffff')),
          stroke: '#9cb4ce',
          strokeWidth: 1,
          originX: 'left',
          originY: 'top',
          isTableCell: true,
          cellRow: r,
          cellCol: c,
        }));
        
        children.push(new fabric.Text(cellData?.text || '', {
          left: ox + c * cellW + cellW / 2,
          top: oy + r * cellH + cellH / 2,
          fontSize: cellData?.fontSize || 11,
          fill: cellData?.textColor || (isHeader ? '#ffffff' : '#1e293b'),
          fontFamily: cellData?.fontFamily || 'Arial',
          fontWeight: cellData?.fontWeight || 'normal',
          fontStyle: cellData?.fontStyle || 'normal',
          originX: 'center',
          originY: 'center',
          isTableText: true,
          cellRow: r,
          cellCol: c,
        }));
      }
    }
    
    const newTable = new fabric.Group(children, {
      left: position?.left ?? left,
      top: position?.top ?? top,
      scaleX: scaleX || 1,
      scaleY: scaleY || 1,
      originX: 'center',
      originY: 'center',
      isTable: true,
      tableRows: rows,
      tableCols: cols,
    });
    
    return newTable;
  }, []);

  /* ── Extract table data for rebuilding ── */
  const extractTableData = useCallback((table) => {
    const objects = table.getObjects();
    const cells = [];
    
    for (let r = 0; r < table.tableRows; r++) {
      for (let c = 0; c < table.tableCols; c++) {
        const cellRect = objects.find(o => o.isTableCell && o.cellRow === r && o.cellCol === c);
        const cellText = objects.find(o => o.isTableText && o.cellRow === r && o.cellCol === c);
        
        cells.push({
          row: r,
          col: c,
          text: cellText?.text || '',
          textColor: cellText?.fill,
          fontSize: cellText?.fontSize,
          fontFamily: cellText?.fontFamily,
          fontWeight: cellText?.fontWeight,
          fontStyle: cellText?.fontStyle,
          bgColor: cellRect?.fill,
        });
      }
    }
    
    return {
      rows: table.tableRows,
      cols: table.tableCols,
      cells,
      left: table.left,
      top: table.top,
      scaleX: table.scaleX,
      scaleY: table.scaleY,
    };
  }, []);

  /* ── Table operations ── */
  const insertTableRow = useCallback((position) => {
    if (!fc.current) return;
    const table = fc.current.getActiveObject();
    if (!table?.isTable) return;
    
    const data = extractTableData(table);
    const insertAt = position === 'above' ? 0 : data.rows;
    
    // Shift existing row indices
    data.cells.forEach(cell => {
      if (cell.row >= insertAt) cell.row += 1;
    });
    
    // Add new row cells
    for (let c = 0; c < data.cols; c++) {
      data.cells.push({
        row: insertAt,
        col: c,
        text: '',
        textColor: '#1e293b',
        bgColor: '#ffffff',
      });
    }
    
    data.rows += 1;
    
    // Remove old table and add new one
    fc.current.remove(table);
    const newTable = rebuildTable(data);
    fc.current.add(newTable);
    fc.current.setActiveObject(newTable);
    fc.current.renderAll();
    pushHistory();
    rerender();
    toast.success(`Row ${position === 'above' ? 'added above' : 'added below'}`);
  }, [extractTableData, rebuildTable, pushHistory, rerender]);

  const deleteTableRow = useCallback(() => {
    if (!fc.current) return;
    const table = fc.current.getActiveObject();
    if (!table?.isTable || table.tableRows <= 1) return;
    
    const data = extractTableData(table);
    const lastRow = data.rows - 1;
    
    // Remove last row cells
    data.cells = data.cells.filter(cell => cell.row !== lastRow);
    data.rows -= 1;
    
    fc.current.remove(table);
    const newTable = rebuildTable(data);
    fc.current.add(newTable);
    fc.current.setActiveObject(newTable);
    fc.current.renderAll();
    pushHistory();
    rerender();
    toast.success('Row deleted');
  }, [extractTableData, rebuildTable, pushHistory, rerender]);

  const insertTableColumn = useCallback((position) => {
    if (!fc.current) return;
    const table = fc.current.getActiveObject();
    if (!table?.isTable) return;
    
    const data = extractTableData(table);
    const insertAt = position === 'left' ? 0 : data.cols;
    
    // Shift existing column indices
    data.cells.forEach(cell => {
      if (cell.col >= insertAt) cell.col += 1;
    });
    
    // Add new column cells
    for (let r = 0; r < data.rows; r++) {
      const isHeader = r === 0;
      data.cells.push({
        row: r,
        col: insertAt,
        text: '',
        textColor: isHeader ? '#ffffff' : '#1e293b',
        bgColor: isHeader ? '#5b9bd5' : (r % 2 === 0 ? '#d6dce5' : '#ffffff'),
      });
    }
    
    data.cols += 1;
    
    fc.current.remove(table);
    const newTable = rebuildTable(data);
    fc.current.add(newTable);
    fc.current.setActiveObject(newTable);
    fc.current.renderAll();
    pushHistory();
    rerender();
    toast.success(`Column ${position === 'left' ? 'added left' : 'added right'}`);
  }, [extractTableData, rebuildTable, pushHistory, rerender]);

  const deleteTableColumn = useCallback(() => {
    if (!fc.current) return;
    const table = fc.current.getActiveObject();
    if (!table?.isTable || table.tableCols <= 1) return;
    
    const data = extractTableData(table);
    const lastCol = data.cols - 1;
    
    data.cells = data.cells.filter(cell => cell.col !== lastCol);
    data.cols -= 1;
    
    fc.current.remove(table);
    const newTable = rebuildTable(data);
    fc.current.add(newTable);
    fc.current.setActiveObject(newTable);
    fc.current.renderAll();
    pushHistory();
    rerender();
    toast.success('Column deleted');
  }, [extractTableData, rebuildTable, pushHistory, rerender]);

  /* ── Table cell formatting ── */
  const updateTableCellColor = useCallback((color, type) => {
    if (!fc.current) return;
    const table = fc.current.getActiveObject();
    if (!table?.isTable) return;
    
    const objects = table.getObjects();
    
    if (type === 'header') {
      // Update all header cells
      objects.forEach(obj => {
        if (obj.isTableCell && obj.cellRow === 0) {
          obj.set('fill', color);
        }
        if (obj.isTableText && obj.cellRow === 0) {
          // Auto-adjust text color for contrast
          const brightness = parseInt(color.slice(1), 16);
          obj.set('fill', brightness > 0x888888 ? '#1e293b' : '#ffffff');
        }
      });
    } else if (type === 'evenRows') {
      objects.forEach(obj => {
        if (obj.isTableCell && obj.cellRow > 0 && obj.cellRow % 2 === 0) {
          obj.set('fill', color);
        }
      });
    } else if (type === 'oddRows') {
      objects.forEach(obj => {
        if (obj.isTableCell && obj.cellRow > 0 && obj.cellRow % 2 === 1) {
          obj.set('fill', color);
        }
      });
    }
    
    fc.current.renderAll();
    pushHistory();
    scheduleAutoSave();
  }, [pushHistory, scheduleAutoSave]);

  const updateTableTextStyle = useCallback((prop, value) => {
    if (!fc.current) return;
    const table = fc.current.getActiveObject();
    if (!table?.isTable) return;
    
    const objects = table.getObjects();
    objects.forEach(obj => {
      if (obj.isTableText) {
        obj.set(prop, value);
      }
    });
    
    fc.current.renderAll();
    pushHistory();
    scheduleAutoSave();
  }, [pushHistory, scheduleAutoSave]);

  /* ── Text-in-shapes: double-click converts shape to labeled group ── */
  const convertToLabeledShape = useCallback((shape) => {
    const c = fc.current;
    if (!c) return;
    const w = shape.getScaledWidth();
    const h = shape.getScaledHeight();
    const cx = shape.left + w / 2;
    const cy = shape.top + h / 2;
    const origFill = shape.fill;

    c.remove(shape);
    c.discardActiveObject();

    // Reset shape for group-relative coordinates
    const shapeClone = shape;
    shapeClone.set({ left: 0, top: 0, originX: 'center', originY: 'center', scaleX: 1, scaleY: 1 });
    if (shapeClone.type === 'rect') shapeClone.set({ width: w, height: h });
    else if (shapeClone.type === 'circle') shapeClone.set({ radius: Math.min(w, h) / 2 });
    else if (shapeClone.type === 'triangle') shapeClone.set({ width: w, height: h });

    const textbox = new fabric.Textbox('Text', {
      left: 0, top: 0, originX: 'center', originY: 'center',
      width: w * 0.75,
      fontSize: Math.max(14, Math.min(24, h * 0.25)),
      fill: isLightColor(origFill) ? '#1a1a2e' : '#ffffff',
      fontFamily: 'Arial', textAlign: 'center',
    });

    const group = new fabric.Group([shapeClone, textbox], {
      left: cx, top: cy, originX: 'center', originY: 'center',
      interactive: true, subTargetCheck: true, isLabeledShape: true,
    });

    c.add(group);
    c.setActiveObject(group);
    c.renderAll();
    rerender();
    scheduleAutoSave();
  }, [rerender, scheduleAutoSave]);

  /* ── Transition handlers ── */
  const handleTransitionChange = useCallback((transition) => {
    const updated = [...slidesRef.current];
    updated[activeRef.current] = { ...updated[activeRef.current], transition };
    setSlides(updated);
    scheduleAutoSave();
  }, [setSlides, scheduleAutoSave]);

  const handleApplyTransitionToAll = useCallback((transition) => {
    const updated = slidesRef.current.map((s) => ({ ...s, transition: { ...transition } }));
    setSlides(updated);
    scheduleAutoSave();
    toast.success('Transition applied to all slides');
  }, [setSlides, scheduleAutoSave]);

  /* ── Bullet / Numbering / Indent ── */
  const toggleBullets = useCallback(() => {
    const obj = fc.current?.getActiveObject();
    if (!obj || (obj.type !== 'textbox' && obj.type !== 'i-text')) return;
    const lines = (obj.text || '').split('\n');
    const allBulleted = lines.every(l => l.trimStart().startsWith('\u2022 ') || l.trim() === '');
    const newLines = lines.map(l => {
      if (l.trim() === '') return l;
      const indent = l.match(/^(\s*)/)[0];
      if (allBulleted) return l.replace(/^(\s*)\u2022 /, '$1');
      const content = l.trimStart().replace(/^[\u2022\d]+[.)]\s*/, '');
      return `${indent}\u2022 ${content}`;
    });
    obj.set('text', newLines.join('\n'));
    fc.current.renderAll();
    pushHistory();
    rerender();
    scheduleAutoSave();
  }, [pushHistory, rerender, scheduleAutoSave]);

  const toggleNumbering = useCallback(() => {
    const obj = fc.current?.getActiveObject();
    if (!obj || (obj.type !== 'textbox' && obj.type !== 'i-text')) return;
    const lines = (obj.text || '').split('\n');
    const allNumbered = lines.filter(l => l.trim()).every(l => /^\s*\d+[.)]\s/.test(l));
    let num = 1;
    const newLines = lines.map(l => {
      if (l.trim() === '') return l;
      const indent = l.match(/^(\s*)/)[0];
      if (allNumbered) return l.replace(/^(\s*)\d+[.)]\s*/, '$1');
      const content = l.trimStart().replace(/^[\u2022\d]+[.)]\s*/, '');
      return `${indent}${num++}. ${content}`;
    });
    obj.set('text', newLines.join('\n'));
    fc.current.renderAll();
    pushHistory();
    rerender();
    scheduleAutoSave();
  }, [pushHistory, rerender, scheduleAutoSave]);

  const indentList = useCallback(() => {
    const obj = fc.current?.getActiveObject();
    if (!obj || (obj.type !== 'textbox' && obj.type !== 'i-text')) return;
    const lines = (obj.text || '').split('\n');
    const newLines = lines.map(l => l.trim() ? '    ' + l : l);
    obj.set('text', newLines.join('\n'));
    fc.current.renderAll();
    pushHistory();
    rerender();
    scheduleAutoSave();
  }, [pushHistory, rerender, scheduleAutoSave]);

  const outdentList = useCallback(() => {
    const obj = fc.current?.getActiveObject();
    if (!obj || (obj.type !== 'textbox' && obj.type !== 'i-text')) return;
    const lines = (obj.text || '').split('\n');
    const newLines = lines.map(l => l.startsWith('    ') ? l.slice(4) : l.replace(/^\s{1,3}/, ''));
    obj.set('text', newLines.join('\n'));
    fc.current.renderAll();
    pushHistory();
    rerender();
    scheduleAutoSave();
  }, [pushHistory, rerender, scheduleAutoSave]);

  /* ── Media preview ── */
  const previewMedia = useCallback(() => {
    const obj = fc.current?.getActiveObject();
    if (!obj?.isMediaEmbed) return;
    setEditorMedia({ type: obj.mediaType, url: obj.mediaUrl, autoplay: true });
  }, []);

  /* ── Update prop ── */
  const updateProp = useCallback((prop, value) => {
    const obj = fc.current?.getActiveObject();
    if (!obj) return;
    obj.set(prop, value);
    obj.setCoords();
    fc.current.renderAll();
    rerender();
  }, [rerender]);

  /* ── Save ── */
  const handleSave = useCallback(() => {
    if (!fc.current) return;
    const final = [...slidesRef.current];
    final[activeRef.current] = { ...final[activeRef.current], canvas: fc.current.toObject(EXTRA_PROPS) };
    const payload = JSON.stringify({ version: 1, slides: final });
    localStorage.removeItem(LS_KEY);
    onSave(payload);
    toast.success('Slides saved');
  }, [onSave]);

  /* ── Preview ── */
  const openPreview = useCallback(() => {
    if (!fc.current) return;
    const updated = [...slidesRef.current];
    updated[activeRef.current] = { ...updated[activeRef.current], canvas: fc.current.toObject(EXTRA_PROPS) };
    setSlides(updated);
    setPreviewIdx(0);
    setPreview(true);
  }, [setSlides]);

  useEffect(() => {
    const mount = previewMountRef.current;
    if (!preview) {
      const canvas = previewFc.current;
      previewFc.current = null;
      if (canvas) { try { canvas.dispose(); } catch { /* ignore */ } }
      return;
    }
    if (!mount) return;
    
    /* Cleanup any existing preview canvas first */
    if (previewFc.current) {
      try { previewFc.current.dispose(); } catch { /* ignore */ }
      previewFc.current = null;
    }
    
    mount.innerHTML = '';
    const el = document.createElement('canvas');
    mount.appendChild(el);
    const pc = new fabric.StaticCanvas(el, { width: CW, height: CH });
    previewFc.current = pc;
    const data = slidesRef.current[0]?.canvas;
    if (data) pc.loadFromJSON(data).then(() => { restoreCustomProps(pc, data); pc.renderAll(); }).catch((e) => { console.warn('Preview init error:', e); pc.renderAll(); });
    return () => {
      const canvas = previewFc.current;
      previewFc.current = null;
      if (canvas) { try { canvas.dispose(); } catch { /* ignore */ } }
      if (mount) mount.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  useEffect(() => {
    if (!preview || !previewFc.current) { setPreviewMediaItems([]); return; }
    if (!previewFc.current.lowerCanvasEl) { setPreviewMediaItems([]); return; }
    const data = slidesRef.current[previewIdx]?.canvas;
    const trans = slidesRef.current[previewIdx]?.transition;
    let transTimer;
    if (previewFc.current.lowerCanvasEl) {
      previewFc.current.clear();
      previewFc.current.backgroundColor = '#ffffff';
    }

    if (data) {
      previewFc.current.loadFromJSON(data).then(() => {
        if (!previewFc.current) return;
        restoreCustomProps(previewFc.current, data);
        fixInteractiveGroups(previewFc.current);

        /* Extract media items from canvas objects (reliable positioning via getBoundingRect) */
        const items = [];
        previewFc.current.getObjects().forEach(obj => {
          if (obj.isMediaEmbed) {
            const bound = obj.getBoundingRect();
            items.push({
              type: obj.mediaType,
              url: obj.mediaUrl,
              autoplay: !!obj.mediaAutoplay,
              hidden: !!obj.mediaHidden,
              left: bound.left,
              top: bound.top,
              width: bound.width,
              height: bound.height,
            });
          }
        });
        setPreviewMediaItems(items);

        previewFc.current.renderAll();
        /* Apply slide transition */
        const anim = getTransitionAnimation(trans);
        if (anim) {
          setPreviewTransAnim(anim);
          transTimer = setTimeout(() => setPreviewTransAnim(''), (trans?.duration || 500) + 100);
        } else {
          setPreviewTransAnim('');
        }
        /* Run entrance animations on objects */
        runObjectAnimations(previewFc.current);
      }).catch((e) => {
        console.warn('Preview nav load error:', e);
        if (previewFc.current) {
          previewFc.current.renderAll();
          setPreviewMediaItems([]);
        }
        setPreviewTransAnim('');
      });
    } else {
      setPreviewMediaItems([]);
      previewFc.current.renderAll();
      setPreviewTransAnim('');
    }
    return () => { if (transTimer) clearTimeout(transTimer); };
  }, [previewIdx, preview]);

  /* ── Keyboard shortcuts ── */
  useEffect(() => {
    const handler = (e) => {
      /* Preview mode: arrow keys navigate slides */
      if (preview) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          setPreviewIdx(p => Math.min(slidesRef.current.length - 1, p + 1));
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          setPreviewIdx(p => Math.max(0, p - 1));
        }
        if (e.key === 'Escape') setPreview(false);
        return;
      }
      if (fc.current?.getActiveObject()?.isEditing) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); redo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        const a = fc.current?.getActiveObject();
        if (a?.type === 'textbox') { a.set('fontWeight', a.fontWeight === 'bold' ? 'normal' : 'bold'); fc.current.renderAll(); rerender(); }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
        e.preventDefault();
        const a = fc.current?.getActiveObject();
        if (a?.type === 'textbox') { a.set('fontStyle', a.fontStyle === 'italic' ? 'normal' : 'italic'); fc.current.renderAll(); rerender(); }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
        e.preventDefault();
        const a = fc.current?.getActiveObject();
        if (a?.type === 'textbox') { a.set('underline', !a.underline); fc.current.renderAll(); rerender(); }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') { e.preventDefault(); duplicateSelected(); }
      /* Delete/Backspace - don't delete when user is typing in input, textarea, or contenteditable */
      const isTypingInInput = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || 
                              document.activeElement?.isContentEditable;
      if (e.key === 'Delete' && !isTypingInInput) { e.preventDefault(); deleteSelected(); }
      if (e.key === 'Backspace' && !fc.current?.getActiveObject()?.isEditing && !isTypingInInput) { e.preventDefault(); deleteSelected(); }
      if (e.key === 'Escape') {
        if (editorMedia) setEditorMedia(null);
        else if (showImgPanel) setShowImgPanel(false);
        else if (showMediaPanel) setShowMediaPanel(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [preview, showImgPanel, showMediaPanel, editorMedia, deleteSelected, duplicateSelected, undo, redo, rerender]);

  /* ── Clipboard Paste Handler (Text & Images) ── */
  useEffect(() => {
    if (preview) return;
    
    const handlePaste = async (e) => {
      if (!fc.current) return;
      
      // Check if user is currently editing a text object - let default behavior handle it
      const activeObj = fc.current.getActiveObject();
      if (activeObj?.isEditing) return;
      
      // Check if focus is in an input field - let default behavior handle it
      const isTypingInInput = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || 
                              document.activeElement?.isContentEditable;
      if (isTypingInInput) return;
      
      const clipboardData = e.clipboardData;
      if (!clipboardData) return;
      
      // Check for image in clipboard
      const items = clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        // Handle image paste
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (!blob) continue;
          
          try {
            // Upload the image first
            const tid = toast.loading('Pasting image...');
            const form = new FormData();
            form.append('file', blob, `pasted-image-${Date.now()}.png`);
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API}/api/upload/image`, form, { 
              headers: { Authorization: `Bearer ${token}` } 
            });
            toast.dismiss(tid);
            
            // Add to canvas
            const img = await fabric.FabricImage.fromURL(`${API}${res.data.url}`, { crossOrigin: 'anonymous' });
            const maxW = CW * 0.6, maxH = CH * 0.6;
            if (img.width > maxW || img.height > maxH) {
              img.scale(Math.min(maxW / img.width, maxH / img.height));
            }
            img.set({ 
              left: CW / 2 - img.getScaledWidth() / 2, 
              top: CH / 2 - img.getScaledHeight() / 2 
            });
            fc.current.add(img);
            fc.current.setActiveObject(img);
            fc.current.renderAll();
            toast.success('Image pasted');
          } catch (err) {
            toast.error('Failed to paste image');
          }
          return; // Image handled, exit
        }
      }
      
      // Handle text paste (if no image was found)
      const text = clipboardData.getData('text/plain');
      if (text && text.trim()) {
        e.preventDefault();
        
        // Create a new text box with the pasted text
        const textObj = new fabric.Textbox(text.trim(), {
          left: CW / 2 - 150,
          top: CH / 2 - 30,
          width: 300,
          fontSize: 24,
          fill: '#1a1a2e',
          fontFamily: 'Arial',
          textAlign: 'left',
        });
        
        // Auto-adjust width based on text length
        const textWidth = textObj.calcTextWidth();
        if (textWidth > 300) {
          textObj.set({ width: Math.min(textWidth + 20, CW - 100) });
        }
        
        fc.current.add(textObj);
        fc.current.setActiveObject(textObj);
        fc.current.renderAll();
        toast.success('Text pasted');
      }
    };
    
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [preview]);

  /* ── Derived ── */
  const active = fc.current?.getActiveObject();
  const isText = active?.type === 'textbox' || active?.type === 'i-text' || active?.type === 'text';
  const isImage = active?.type === 'image';
  const isMedia = active?.isMediaEmbed;
  const isTable = active?.isTable;
  const isShape = active && !isText && !isImage && !isMedia && !isTable && active.type !== 'group';
  const hasSelection = !!active;

  /* ── Render ── */
  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-background flex flex-col" data-testid="slide-editor">
      {/* RIBBON */}
      <RibbonMenu
        onClose={onClose} onSave={handleSave} openPreview={openPreview}
        addText={addText} addShape={addShape}
        onShowImgPanel={() => setShowImgPanel((p) => !p)}
        onShowVideoPanel={() => setShowMediaPanel((p) => (p === 'video' ? null : 'video'))}
        onShowAudioPanel={() => setShowMediaPanel((p) => (p === 'audio' ? null : 'audio'))}
        deleteSelected={deleteSelected} duplicateSelected={duplicateSelected}
        bringForward={bringForward} sendBackward={sendBackward}
        undo={undo} redo={redo}
        canUndo={historyIdx.current > 0}
        canRedo={historyIdx.current < historyStack.current.length - 1}
        active={active} isText={isText} isShape={isShape} isImage={isImage} isMedia={isMedia} isTable={isTable} hasSelection={hasSelection}
        updateProp={updateProp}
        bgColor={bgColor} changeBgColor={changeBgColor}
        onBgImgUpload={() => bgImgInput.current?.click()}
        clearBgImage={clearBgImage} hasBgImage={!!fc.current?.backgroundImage}
        setImageAsBackground={setImageAsBackground}
        currentTransition={slides[activeIdx]?.transition || { type: 'none', duration: 500 }}
        onTransitionChange={handleTransitionChange}
        onApplyTransitionToAll={handleApplyTransitionToAll}
        toggleBullets={toggleBullets}
        toggleNumbering={toggleNumbering}
        indentList={indentList}
        outdentList={outdentList}
        onPreviewMedia={previewMedia}
        addTable={addTable}
        insertTableRow={insertTableRow}
        deleteTableRow={deleteTableRow}
        insertTableColumn={insertTableColumn}
        deleteTableColumn={deleteTableColumn}
        updateTableCellColor={updateTableCellColor}
        updateTableTextStyle={updateTableTextStyle}
      />

      {/* INPUT PANELS */}
      {showImgPanel && (
        <div className="absolute top-[5.5rem] left-48 z-10 bg-popover border rounded-lg shadow-xl p-3 w-72" data-testid="image-panel">
          <Label className="text-xs font-medium">Add Image by URL</Label>
          <div className="flex gap-1 mt-1">
            <Input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="https://..." className="text-xs h-8" data-testid="image-url-input" />
            <Button size="sm" className="h-8" onClick={() => addImageFromUrl(imgUrl)} data-testid="image-url-add">Add</Button>
          </div>
          <div className="text-center text-[10px] text-muted-foreground my-1.5">or</div>
          <Button variant="outline" size="sm" className="w-full h-8 text-xs" onClick={() => imgInput.current?.click()} data-testid="image-upload-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> Upload from device
          </Button>
          <Button variant="ghost" size="sm" className="w-full h-7 text-xs mt-1 text-muted-foreground" onClick={() => setShowImgPanel(false)}>Close</Button>
        </div>
      )}
      {showMediaPanel && (
        <div className="absolute top-[5.5rem] left-72 z-10 bg-popover border rounded-lg shadow-xl p-3 w-80" data-testid="media-panel">
          <Label className="text-xs font-medium">{showMediaPanel === 'video' ? 'Video' : 'Audio'}</Label>
          <div className="flex gap-1 mt-1">
            <Input value={mediaUrlInput} onChange={(e) => setMediaUrlInput(e.target.value)} placeholder="Paste URL..." className="text-xs h-8" data-testid="media-url-input" />
            <Button size="sm" className="h-8" onClick={() => addMediaEmbed(showMediaPanel, mediaUrlInput)} data-testid="media-url-add">Add</Button>
          </div>
          <div className="text-center text-[10px] text-muted-foreground my-1.5">or</div>
          <Button variant="outline" size="sm" className="w-full h-8 text-xs" onClick={() => mediaFileInput.current?.click()} data-testid="media-upload-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> Upload {showMediaPanel} from device
          </Button>
          <Button variant="ghost" size="sm" className="w-full h-7 text-xs mt-1 text-muted-foreground" onClick={() => setShowMediaPanel(null)}>Close</Button>
        </div>
      )}

      {/* MAIN AREA */}
      <div className="flex-1 flex overflow-hidden">
        <SlideThumbnails
          slides={slides} activeIdx={activeIdx} thumbs={thumbs}
          switchSlide={switchSlide} addSlide={addSlide}
          duplicateSlide={duplicateSlide} moveSlide={moveSlide} deleteSlide={deleteSlide}
        />
        <div ref={wrapRef} className="flex-1 flex items-center justify-center bg-neutral-100 dark:bg-neutral-900 overflow-hidden relative">
          <div className="shadow-2xl rounded" style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}>
            <div ref={canvasMountRef} data-testid="canvas-mount" />
          </div>
        </div>
        {hasSelection && <PropertiesPanel active={active} updateProp={updateProp} fc={fc} rerender={rerender} />}
      </div>

      {/* STATUS BAR */}
      <div className="h-8 border-t bg-muted/20 flex items-center justify-between px-4 text-[10px] text-muted-foreground shrink-0">
        <div className="flex items-center gap-3">
          <span data-testid="slide-counter">Slide {activeIdx + 1} of {slides.length}</span>
          <span>{CW}x{CH}</span>
          <span>Zoom: {zoomLevel}%</span>
          {slides[activeIdx]?.transition?.type !== 'none' && (
            <span className="text-primary">Transition: {slides[activeIdx].transition.type}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span>{autoSaved ? 'Auto-saved' : ''}</span>
          <span className="text-muted-foreground/50">Ctrl+Z Undo | Ctrl+Y Redo | Del Delete | Dbl-click shape for text</span>
        </div>
      </div>

      {/* HIDDEN FILE INPUTS */}
      <input ref={imgInput} type="file" accept="image/*" hidden onChange={handleImgUpload} />
      <input ref={bgImgInput} type="file" accept="image/*" hidden onChange={handleBgImgUpload} />
      <input ref={mediaFileInput} type="file" accept={showMediaPanel === 'video' ? 'video/*' : 'audio/*'} hidden onChange={handleMediaFileUpload} />

      {/* IN-EDITOR MEDIA PLAYER */}
      {editorMedia && (
        <div className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center" onClick={() => setEditorMedia(null)} data-testid="editor-media-overlay">
          <div className="bg-background rounded-xl shadow-2xl p-4 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold capitalize">{editorMedia.type} Preview</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditorMedia(null)} data-testid="close-media-preview">
                <X className="w-4 h-4" />
              </Button>
            </div>
            {editorMedia.type === 'video' ? (
              /* Check if it's a YouTube/Vimeo URL - use iframe embed */
              isEmbedUrl(editorMedia.url) ? (
                <div className="w-full aspect-video">
                  <EmbedPlayer url={editorMedia.url} testId="editor-embed-player" />
                </div>
              ) : (
                /* Regular video - use minimal custom player */
                <div className="w-full max-h-[60vh] aspect-video">
                  <MinimalVideoPlayer 
                    src={editorMedia.url} 
                    autoPlay={true} 
                    testId="editor-video-player"
                    className="w-full h-full"
                  />
                </div>
              )
            ) : (
              <audio 
                src={editorMedia.url} 
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                controls 
                autoPlay 
                className="w-full" 
                data-testid="editor-audio-player"
              />
            )}
          </div>
        </div>
      )}

      {/* INLINE CELL EDITOR */}
      {cellEditor && (
        <div 
          className="fixed inset-0 z-[10001]"
          onClick={() => saveCellEdit(cellEditorRef.current?.value || '')}
          data-testid="cell-editor-overlay"
        >
          <input
            ref={cellEditorRef}
            type="text"
            defaultValue={cellEditor.text}
            autoFocus
            className="absolute border-2 border-blue-500 bg-white px-1 text-sm outline-none"
            style={{
              left: cellEditor.rect.left,
              top: cellEditor.rect.top,
              width: Math.max(cellEditor.rect.width, 80),
              height: cellEditor.rect.height,
              color: cellEditor.isHeader ? '#ffffff' : '#1e293b',
              backgroundColor: cellEditor.isHeader ? '#5b9bd5' : '#ffffff',
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveCellEdit(e.target.value);
              } else if (e.key === 'Escape') {
                setCellEditor(null);
              } else if (e.key === 'Tab') {
                e.preventDefault();
                // Save current cell and move to next
                saveCellEdit(e.target.value);
              }
            }}
            data-testid="cell-editor-input"
          />
        </div>
      )}

      {/* PREVIEW */}
      {preview && (
        <div className="fixed inset-0 z-[10000] bg-black/95 flex flex-col items-center justify-center" data-testid="slide-preview">
          <style>{TRANSITION_CSS}</style>
          <div className="absolute top-4 right-4 z-10">
            <Button variant="ghost" size="icon" onClick={() => setPreview(false)} className="text-white hover:bg-white/20" data-testid="preview-close">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="shadow-2xl rounded-lg overflow-hidden relative"
              style={previewTransAnim ? { animation: previewTransAnim } : undefined}
            >
              <div ref={previewMountRef} />
              {/* Overlay actual media players on embeds */}
              {previewMediaItems.map((item, i) =>
                item.hidden ? (
                  <audio key={i} src={item.url} autoPlay={item.autoplay} data-testid={`preview-audio-hidden-${i}`} style={{ display: 'none' }} />
                ) : (
                  <div key={i} className="absolute" style={{
                    left: item.left, top: item.top,
                    width: item.width, height: item.height,
                  }}>
                    {item.type === 'video' ? (
                      /* Check if it's a YouTube/Vimeo URL - use iframe embed */
                      isEmbedUrl(item.url) ? (
                        <EmbedPlayer url={item.url} testId={`preview-embed-${i}`} />
                      ) : (
                        /* Regular video - use minimal custom player (play/pause only) */
                        <MinimalVideoPlayer 
                          src={item.url} 
                          autoPlay={item.autoplay} 
                          testId={`preview-video-${i}`}
                        />
                      )
                    ) : (
                      /* Audio: clean player with no download option */
                      <div className="w-full h-full flex items-center justify-center">
                        <audio 
                          src={item.url} 
                          controlsList="nodownload noplaybackrate"
                          disablePictureInPicture
                          controls 
                          autoPlay={item.autoplay} 
                          className="w-full" 
                          style={{ minWidth: 0, height: '100%' }} 
                          data-testid={`preview-audio-${i}`} 
                        />
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
          <div className="h-16 flex items-center gap-6 shrink-0">
            <Button variant="ghost" size="icon" onClick={() => setPreviewIdx(Math.max(0, previewIdx - 1))} disabled={previewIdx === 0} className="text-white hover:bg-white/20" data-testid="preview-prev">
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <span className="text-white text-sm" data-testid="preview-counter">
              Slide {previewIdx + 1} of {slides.length}
            </span>
            <Button variant="ghost" size="icon" onClick={() => setPreviewIdx(Math.min(slides.length - 1, previewIdx + 1))} disabled={previewIdx === slides.length - 1} className="text-white hover:bg-white/20" data-testid="preview-next">
              <ChevronRight className="w-6 h-6" />
            </Button>
            <span className="text-white/40 text-[10px] ml-4">Arrow keys to navigate | Esc to close</span>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

/* Helper: determine if a color is light (for text contrast in shapes) */
function isLightColor(color) {
  if (!color || typeof color !== 'string') return true;
  let hex = color.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  if (hex.length !== 6) return true;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 128;
}
